// ==============================================
// /s/:token — a document someone shared from Ship's Papers (insurer, marina,
// agent). Public: no MyPolaris login. Shows what it is and offers view/download.
// Readability mandate: 16px+ body.
// ==============================================
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DocumentTextIcon, ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { errMsg } from '../components/papers/shared';

const fmtDate = (iso: string | null) => (iso ? new Date(iso.length === 10 ? iso + 'T00:00:00' : iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const fmtBytes = (b: number) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function SharedDocumentPage() {
  const { token = '' } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ['papers-public', token], queryFn: () => api.papersPublicInfo(token), retry: false });
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const open = async (download: boolean) => {
    setBusy(true);
    setOpenError(null);
    try {
      const blob = await api.papersPublicFile(token, download);
      const url = URL.createObjectURL(blob);
      if (download) { const a = document.createElement('a'); a.href = url; a.download = data?.file?.name ?? 'document'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 10_000); }
      else setPreview(url);
    } catch (e) {
      setOpenError(errMsg(e, 'The file could not be opened. Try again, or ask the person who shared it for a new link.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="px-6 py-4 border-b border-slate-800 text-lg font-bold">MyPolaris · Ship's Papers</header>
      <main className="max-w-3xl mx-auto p-6 space-y-4">
        {isLoading ? <p className="text-slate-300">Opening…</p> : error || !data ? (
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
            <h1 className="text-xl font-bold mb-2">This link has expired or been withdrawn</h1>
            <p className="text-base text-slate-300">Ask the person who shared it for a new link.</p>
          </div>
        ) : (<>
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6">
            <div className="text-sm text-slate-400">{data.section} · {data.type}{data.vessel_name ? ` · ${data.vessel_name}` : ''}</div>
            <h1 className="text-2xl font-bold flex items-center gap-2 mt-1"><DocumentTextIcon className="w-7 h-7 text-amber-400" /> {data.title}</h1>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-base mt-3">
              {data.issuer && <><dt className="text-slate-400">Issued by</dt><dd>{data.issuer}</dd></>}
              {data.issued_on && <><dt className="text-slate-400">Issued</dt><dd>{fmtDate(data.issued_on)}</dd></>}
              {data.expires_on && <><dt className="text-slate-400">Expires</dt><dd>{fmtDate(data.expires_on)}</dd></>}
            </dl>
            <div className="flex flex-wrap gap-2 mt-5">
              {data.file && <button type="button" disabled={busy} onClick={() => open(false)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-400 text-slate-900 text-base font-bold hover:brightness-105 disabled:opacity-60"><EyeIcon className="w-5 h-5" /> View</button>}
              {data.file && <button type="button" disabled={busy} onClick={() => open(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-600 text-base font-semibold hover:border-amber-400 disabled:opacity-60"><ArrowDownTrayIcon className="w-5 h-5" /> Download {data.file.name} ({fmtBytes(data.file.size_bytes)})</button>}
              {data.url && <a href={data.url} target="_blank" rel="noreferrer" className="inline-flex items-center px-4 py-2.5 rounded-lg border border-slate-600 text-base font-semibold hover:border-amber-400">Open link</a>}
            </div>
            {openError && <p role="alert" className="text-base text-red-200 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2 mt-4">{openError}</p>}
            <p className="text-sm text-slate-500 mt-4">This link stops working on {fmtDate(data.expires_at)}{data.label ? ` · shared for ${data.label}` : ''}. Shared from MyPolaris Ship's Papers.</p>
          </div>
          {preview && (data.file?.mime.startsWith('image/') ? <img src={preview} alt={data.title} className="max-h-[80vh] w-full object-contain rounded-xl border border-slate-700" /> : <iframe title={data.title} src={preview} className="w-full h-[80vh] rounded-xl border border-slate-700 bg-white" />)}
        </>)}
      </main>
    </div>
  );
}
